import React, {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { isNaN, isNumber } from 'lodash';
import {useTranslations} from "use-intl";

import { Nullable } from '@tager/web-core';

import { colors } from '@/constants/theme';
import RangeInput from '@/components/Input/RangeInput';
import {
  getFullWidth,
  handleRange,
  getWidth,
  getMargin,
} from '@/components/Filter/components/Range/helpers';

import { Props } from './types';

interface RangeCss extends CSSProperties {
  '--range-width': string;
  '--range-margin': string;
}

const Range = ({ field, value, onChange }: Props) => {
  const [minValue, setMinValue] = useState(value[0]);
  const [maxValue, setMaxValue] = useState(value[1]);
  const [lastUpdatedInput, setLastUpdatedInput] =
    useState<Nullable<number>>(null);
  const [rangeMargin, setMargin] = useState(
    getMargin(
      value[0],
      field.valueFrom ?? 0,
      getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
    )
  );
  const [rangeWidth, setWidth] = useState(
    getWidth(
      rangeMargin,
      field.valueTo ?? 0,
      value[1],
      getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
    )
  );

  const prevValue = useRef(value);

  const t = useTranslations();

  const setFocus = (index: number) =>
    lastUpdatedInput !== null && lastUpdatedInput === index;

  const onChangeValue = (inputValue: string, index: number) => {
    let fixedValue: number = Number(inputValue.trim());
    if (!isNumber(fixedValue) || isNaN(fixedValue)) {
      return null;
    }
    if (field?.valueTo && fixedValue > field.valueTo) {
      fixedValue = index === 0 ? maxValue - 1 : field.valueTo;
    }
    if (index === 0 && fixedValue === minValue) {
      return null;
    }
    index === 0 ? setMinValue(fixedValue) : setMaxValue(fixedValue);
  };

  const onRangeChangeFrom = (value: string) => {
    const margin = getMargin(
      minValue,
      field.valueFrom ?? 0,
      getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
    );
    setMargin((pre) => margin);
    setWidth((pre) =>
      getWidth(
        margin,
        field.valueTo ?? 0,
        maxValue,
        getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
      )
    );
    onChangeValue(value, 0);
  };

  const onRangeChangeTo = (value: string) => {
    onChangeValue(value, 1);
    const width = getWidth(
      rangeMargin,
      field.valueTo ?? 0,
      maxValue,
      getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
    );
    setWidth(width);
    onChangeValue(value, 1);
  };

  useLayoutEffect(() => {
    if (maxValue < minValue) {
      const fixedMaxValue =
        maxValue > (field.valueFrom ?? 0) ? maxValue : field.valueFrom ?? 0;
      const width =
        ((minValue - fixedMaxValue) /
          getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)) *
        100;
      const margin =
        fixedMaxValue > (field.valueFrom ?? 0)
          ? ((fixedMaxValue - (field.valueFrom ?? 0)) /
              getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)) *
            100
          : 0;
      setWidth((prevV) => width);
      setMargin((prevV) => margin);
    } else {
      const width = getWidth(
        rangeMargin,
        field.valueTo ?? 0,
        maxValue,
        getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
      );
      const margin =
        minValue > (field.valueFrom ?? 0)
          ? getMargin(
              minValue,
              field.valueFrom ?? 0,
              getFullWidth(field.valueFrom ?? 0, field.valueTo ?? 0)
            )
          : 0;
      setMargin((preV) => margin);
      setWidth((prevV) => width);
    }
  }, [field.valueFrom, field.valueTo, maxValue, minValue, rangeMargin]);

  useEffect(() => {
    prevValue.current = value;
  }, [value]);

  return (
    <Component>
      <Content
        style={
          {
            '--range-width': `${rangeWidth}%`,
            '--range-margin': `${rangeMargin}%`,
          } as RangeCss
        }
      >
        <RangeWrapper>
          <InputRange
            value={minValue}
            type="range"
            min={field.rangeFrom ?? 0}
            max={field.rangeTo ?? 0}
            step={1}
            onMouseUp={() => {
              handleRange(field, value, minValue, 'valueFrom', onChange);
            }}
            onChange={(e) => {
              onRangeChangeFrom(e.target.value);
            }}
          />
          <InputRange
            value={maxValue}
            type="range"
            min={field.rangeFrom ?? 0}
            max={field.rangeTo ?? 0}
            step={1}
            onMouseUp={() => {
              handleRange(field, value, maxValue, 'valueTo', onChange);
            }}
            onChange={(e) => {
              onRangeChangeTo(e.target.value);
            }}
          />
        </RangeWrapper>
        <Details>
          {value.map((currentValue, index) => (
            <Row key={index}>
              <RangeInput
                isOnFocus={setFocus(index)}
                value={index === 0 ? minValue : maxValue}
                mask={index === 0 ? t('dimensions.from') :t('dimensions.to')}
                onBlur={(currentValue) => {
                  const type = index === 0 ? 'valueFrom' : 'valueTo';
                  handleRange(
                    field,
                    value,
                    index === 0 ? minValue : maxValue,
                    type,
                    onChange
                  );
                }}
                onChange={(currentValue) => {
                  onChangeValue(currentValue, index);
                  setLastUpdatedInput(index);
                }}
              />
            </Row>
          ))}
        </Details>
      </Content>
    </Component>
  );
};

export default Range;

const Component = styled.div`
  position: relative;
`;

const Content = styled.div``;

const Details = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

const Row = styled.div`
  position: relative;
  height: 34px;
  overflow: hidden;
  border-radius: 5px;
  border: 1px solid ${colors.gray70};
`;

const RangeWrapper = styled.div`
  position: relative;
  margin: 10px 0;
  height: 46px;

  &:before,
  &:after {
    position: absolute;
    content: '';
    display: block;
    top: 50%;
    transform: translateY(-50%);
    height: 5px;
    border-radius: 5px;
  }
  &:after {
    width: var(--range-width, 100%);
    margin-left: var(--range-margin, 0);
    background: ${colors.primary};
  }
  &:before {
    width: 100%;
    background: ${colors.gray70};
  }
`;

const InputRange = styled.input`
  position: relative;

  display: flex;
  align-items: center;
  cursor: pointer;
  height: 5px;
  background: none;
  border-radius: 5px;
  width: 100%;
  -webkit-appearance: none;
  pointer-events: none;

  &:last-child {
    top: calc(100% - 10px);
  }

  &::-webkit-slider-runnable-track {
    -webkit-appearance: none;
  }

  &::-webkit-slider-thumb {
    position: relative;
    z-index: 1;
    -webkit-appearance: none;
    width: 28px;
    height: 28px;
    background: white;
    border: 2px solid ${colors.primary};
    border-radius: 50%;
    pointer-events: all;
    top: calc(23px - 2px);
  }
  &:last-child::-webkit-slider-thumb {
    top: calc(-23px + 3px);
  }

  &::-moz-range-thumb {
  }
`;
